import React, { useState } from 'react';
import { CheckCircle2, Loader2, Upload } from 'lucide-react';
import { hcApi, hcError } from './api';

export const TUITION_FIELDS = [
  ['institution_name', 'Institution name'], ['institution_address', 'Address'], ['institution_city_state_zip', 'City, state and zip code'],
  ['program_name', 'Course, program or concentration name'], ['enrollment_date', 'Enrollment date', 'date'], ['program_start_date', 'Program start date', 'date'],
  ['payee_name', 'Payee name'], ['total_tuition', 'Total tuition amount'], ['payee_address', 'Payee address'], ['payee_city_state_zip', 'Payee city, state and zip code'],
];
const MAX = 10 * 1024 * 1024;
const EXTS = ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'heif'];
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/jpeg,image/png,image/heic,image/heif';
const label = 'text-sm font-semibold text-[#363636]';

export const DocUpload = ({ kind, title, value, onChange, field }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!EXTS.includes(ext)) { setError("This file type isn't supported. Please upload a PDF, JPG, PNG or HEIC file."); return; }
    if (file.size > MAX) { setError('This file is too large. Please upload a file that is 10 MB or smaller.'); return; }
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('kind', kind); fd.append('file', file);
      const { data } = await hcApi.post('/supportive-service-requests/uploads', fd);
      onChange(data);
    } catch (err) { setError(hcError(err)); onChange(null); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`hc-ssr-upload-${kind}`}>
      <p className={label}>{title} <span className="text-red-600">*</span></p>
      <label className={`${field} mt-1 flex items-center gap-3 cursor-pointer hover:border-[#922A8C]/50`} data-testid={`hc-ssr-upload-${kind}-label`}>
        {busy ? <Loader2 className="w-5 h-5 animate-spin text-[#922A8C]" /> : value ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Upload className="w-5 h-5 text-[#922A8C]" />}
        <span className="text-sm text-gray-600 truncate" data-testid={`hc-ssr-upload-${kind}-name`}>{busy ? 'Uploading...' : value ? value.filename : 'Take a photo or choose a file'}</span>
        <input type="file" accept={ACCEPT} onChange={pick} disabled={busy} className="sr-only" data-testid={`hc-ssr-upload-${kind}-input`} />
      </label>
      <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG or HEIC, up to 10 MB</p>
      {error && <p className="text-red-600 text-sm mt-1" data-testid={`hc-ssr-upload-${kind}-error`}>{error}</p>}
    </div>
  );
};

export const HcSsrTuitionFields = ({ values, setValues, docs, setDocs, field }) => {
  const input = ([key, text, type]) => (
    <div key={key}>
      <label className={label}>{text} <span className="text-red-600">*</span></label>
      <input type={type || 'text'} value={values[key] || ''} required maxLength={300} onChange={(e) => setValues({ ...values, [key]: e.target.value })}
        className={field} data-testid={`hc-ssr-tuition-${key}`} />
    </div>
  );
  return (
    <div className="space-y-6 rounded-2xl bg-[#922A8C]/5 p-4 sm:p-5" data-testid="hc-ssr-tuition-section">
      <div className="space-y-4">
        <h3 className="font-bold text-[#363636]">Educational institution details</h3>
        {TUITION_FIELDS.slice(0, 6).map(input)}
      </div>
      <div className="space-y-4">
        <h3 className="font-bold text-[#363636]">Award and payee details</h3>
        {TUITION_FIELDS.slice(6).map(input)}
      </div>
      <div className="space-y-4">
        <h3 className="font-bold text-[#363636]">Documents</h3>
        <DocUpload kind="tuition_bill" title="Tuition bill" value={docs.tuition_bill} onChange={(v) => setDocs((d) => ({ ...d, tuition_bill: v }))} field={field} />
        <DocUpload kind="acceptance_letter" title="Acceptance letter" value={docs.acceptance_letter} onChange={(v) => setDocs((d) => ({ ...d, acceptance_letter: v }))} field={field} />
      </div>
    </div>
  );
};
